import React, { memo, type ReactElement, type ReactNode } from 'react';
import { Avatar, Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { testIds } from '../../../tests';
import { CF_NEU_BLD } from '../../constants';

import './SodUserCard.css';

/**
 * Props interface for the SodUserCard component
 */
interface SodUserCardProps {
  firstName: string | undefined;
  lastName: string | undefined;
  userName: string | undefined;
  link?: string;
  trailContent?: ReactNode;
  className?: string;
}

/**
 * SodUserCard
 *
 * A card component that displays user information for SOD (Separation of Duties)
 *
 * @typedef {Object} SodUserCardProps
 * @property {string} [userName] - The name of the user to display
 * @property {string} [avatarUrl] - URL for the user's avatar/profile picture
 * @property {React.ReactNode} [children] - Optional child elements to render within the card
 *
 * @example
 * ```tsx
 * <SodUserCard
 *   userName="John Doe"
 *   userEmail="john.doe@example.com"
 *   userRole="Administrator"
 *   avatarUrl="/path/to/avatar.jpg"
 * >
 *   <AdditionalContent />
 * </SodUserCard>
 * ```
 */
function SodUserCard({
  firstName,
  lastName,
  userName,
  link,
  trailContent,
  className,
}: SodUserCardProps): ReactElement | null {
  const wrapperClasses = cx('Sod-UserCard', className);

  const isFirstName = Boolean(firstName && firstName.trim().length !== 0);
  const isLastName = Boolean(lastName && lastName.trim().length !== 0);
  const isUserName = Boolean(userName && userName.trim().length !== 0);

  if (!isFirstName && !isLastName && !isUserName) {
    return null;
  }

  if (!isFirstName && !isLastName) {
    return (
      <Box className={wrapperClasses} dataTestId={testIds.SOD_USER_CARD}>
        <Avatar size='xxSmall' />
        {trailContent ?? (
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            className={CF_NEU_BLD}>
            {userName}
          </Typography>
        )}
      </Box>
    );
  }

  const tempFirstName = firstName ?? 'Not ';
  const tempLastName = lastName ?? 'Available';

  const tempUserName = `${tempFirstName} ${tempLastName}`;
  const initials = `${tempFirstName[0].toUpperCase()}${tempLastName[0].toUpperCase()}`;

  return (
    <Box className={wrapperClasses} dataTestId={testIds.SOD_USER_CARD}>
      {link ? (
        <Avatar alt={userName} size='xxSmall' src={link} />
      ) : (
        <Avatar size='xxSmall'>{initials}</Avatar>
      )}
      {trailContent ?? (
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          className={CF_NEU_BLD}>
          {tempUserName}
        </Typography>
      )}
    </Box>
  );
}

SodUserCard.defaultProps = {
  link: null,
  trailContent: null,
  className: null,
};

export default memo(SodUserCard);
